import React, { useEffect, useState } from "react";
import {
  X, ChevronLeft, ChevronRight, Radio, Camera, Sparkles, Users,
  Music2, LayoutGrid, Send, Settings as SettingsIcon, BookOpen, Check,
} from "lucide-react";

/**
 * GoLiveTutorial — first-run / "Learn how to go LIVE" modal.
 *
 * 7-step walkthrough that mirrors what's actually on screen in the
 * mobile studio so creators can map each lesson to a real control
 * without leaving the page. Closes back to the studio on the final
 * step ("You're ready").
 *
 * Open via `onOpenTutorial` from MobileLiveStudio's empty state or the
 * "Learn" entry that DirectorsMode adds to the studio's Settings sheet.
 *
 * Tutorial state is persisted to localStorage so we can stop nagging
 * creators after they've seen it once.
 */
const STORAGE_KEY = "tapin_studio_tutorial_seen_v1";

const STEPS = [
  {
    icon: Radio,
    title: "1. Tap Go LIVE",
    body: "The big red button at the bottom right kicks off your broadcast. The first time, your phone will ask for camera + mic permission — tap Allow.",
    tips: ["Allow camera", "Allow microphone", "Sit in a quiet, well-lit room"],
  },
  {
    icon: Camera,
    title: "2. Pick your camera",
    body: "Flip front ↔ back at the top of the right-side icon column. Want a phone-as-cam? Use Cameras at the bottom to scan a QR and add a second device.",
    tips: ["Flip (top of side rail)", "Cameras → +Add phone cam"],
  },
  {
    icon: Sparkles,
    title: "3. Look pro with Effects",
    body: "Effects opens Studio FX — toggle Person Cutout to remove your background instantly (no green wall needed) or pick a virtual studio backdrop from the gallery.",
    tips: ["Person Cutout = AI", "Virtual backgrounds", "AI-generated backdrops"],
  },
  {
    icon: Users,
    title: "4. Invite guests",
    body: "Guests opens the cohost manager. Share an invite link or QR — when they accept, they show up as avatars in the top bar and can be promoted to Cam 1/2/3.",
    tips: ["Copy invite link", "Show QR code", "Promote to on-air"],
  },
  {
    icon: LayoutGrid,
    title: "5. Choose a Layout",
    body: "Solo, PiP, 50/50 split, or 2x2 grid — Layout switches the on-screen composition for your audience in one tap. Great for hosting interviews or panels.",
    tips: ["Solo", "PiP", "50/50", "2x2 grid"],
  },
  {
    icon: Music2,
    title: "6. Add Music & Mixer",
    body: "Music opens the deck — search YouTube/SoundCloud, queue tracks, and ride the EQ. Newsroom overlays, soundboard SFX, and Announcer FX live here too.",
    tips: ["YouTube/SoundCloud", "Mixer EQ", "Soundboard"],
  },
  {
    icon: Send,
    title: "7. Engage your audience",
    body: "Tap the comment box to chat with viewers. Gifts + tips drop straight into your wallet. Share spreads the link to other platforms. Have fun!",
    tips: ["Chat with viewers", "Gifts + Tips", "Share to socials"],
  },
];

export default function GoLiveTutorial({ open, onClose }) {
  const [stepIdx, setStepIdx] = useState(0);
  // Keyboard support — Escape closes, Arrow keys page through steps.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { onClose?.(); }
      else if (e.key === "ArrowRight") { setStepIdx((i) => Math.min(STEPS.length - 1, i + 1)); }
      else if (e.key === "ArrowLeft")  { setStepIdx((i) => Math.max(0, i - 1)); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  const step = STEPS[stepIdx];
  const Icon = step.icon;
  const isLast = stepIdx === STEPS.length - 1;

  const finish = () => {
    try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch { /* ignore */ }
    setStepIdx(0);
    onClose?.();
  };

  return (
    <div
      data-testid="go-live-tutorial"
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
      onClick={(e) => { if (e.target === e.currentTarget) finish(); }}
    >
      <div className="w-full max-w-md bg-gradient-to-b from-[#1a1230] to-[#0d0a1d] border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-2">
          <div className="inline-flex items-center gap-2 text-tv-accent text-[10px] uppercase tracking-[0.2em] font-black">
            <BookOpen className="w-3 h-3" /> Learn how to go LIVE
          </div>
          <button
            type="button"
            onClick={finish}
            data-testid="tutorial-close"
            aria-label="Close tutorial"
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Step indicator dots */}
        <div className="flex gap-1.5 px-4 mb-3" role="tablist" aria-label="Tutorial progress">
          {STEPS.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setStepIdx(i)}
              data-testid={`tutorial-step-${i + 1}-dot`}
              aria-label={`Go to step ${i + 1}`}
              aria-current={i === stepIdx}
              className={`h-1 flex-1 rounded-full transition ${
                i === stepIdx ? "bg-tv-accent" : i < stepIdx ? "bg-tv-accent/40" : "bg-white/15"
              }`}
            />
          ))}
        </div>

        {/* Body */}
        <div className="px-6 pb-2">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-tv-accent/15 border border-tv-accent/30 flex items-center justify-center text-tv-accent">
            <Icon className="w-7 h-7" />
          </div>
          <h2 data-testid="tutorial-title" className="text-white font-display font-black text-xl text-center tracking-tighter">
            {step.title}
          </h2>
          <p className="text-white/65 text-sm text-center mt-2 leading-relaxed">
            {step.body}
          </p>
          {step.tips?.length > 0 && (
            <ul className="mt-4 space-y-1.5 max-w-xs mx-auto">
              {step.tips.map((tip) => (
                <li key={tip} className="flex items-start gap-2 text-xs text-white/75">
                  <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Nav */}
        <div className="px-4 py-4 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setStepIdx(Math.max(0, stepIdx - 1))}
            disabled={stepIdx === 0}
            data-testid="tutorial-prev"
            className="w-11 h-11 rounded-full bg-white/8 hover:bg-white/15 text-white flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 text-center text-[10px] uppercase tracking-[0.25em] text-white/45 font-bold">
            Step {stepIdx + 1} / {STEPS.length}
          </div>
          {isLast ? (
            <button
              type="button"
              onClick={finish}
              data-testid="tutorial-finish"
              className="bg-tv-accent hover:bg-yellow-300 text-black font-display font-black uppercase tracking-wider text-xs px-5 py-3 rounded-full inline-flex items-center gap-1.5"
            >
              <Radio className="w-4 h-4" /> I'm ready
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStepIdx(stepIdx + 1)}
              data-testid="tutorial-next"
              className="bg-white text-black font-display font-black uppercase tracking-wider text-xs px-5 py-3 rounded-full inline-flex items-center gap-1.5"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Helper for parents that want to auto-open the tutorial on a creator's
 *  first visit to the studio. */
export function tutorialHasBeenSeen() {
  try { return !!localStorage.getItem(STORAGE_KEY); }
  catch { return false; }
}
